import { useState, type ReactNode } from 'react'
import { MIN_PASSWORD_LENGTH } from '@diana/shared'
import { authClient, signOutAndLeave, useSession } from '../../lib/auth.js'
import { api, ApiError } from '../../lib/api.js'
import { Column, Main, TopTitle } from '../../classroom/Shell.js'
import { Button } from '../../classroom/ui.js'

/**
 * Diana's own login details (Studio-Account.png): the e-mail she signs in
 * with, the password, and signing out. One „Zapisz zmiany” saves whichever
 * section she filled in; each section says under its fields how it went.
 *
 * This screen exists because the seed creates her account from environment
 * variables and then never touches it again — without it the only way to
 * change that first password is a developer with a database console.
 */
export function AccountPage() {
  const { data: session, refetch } = useSession()
  const currentEmail = session?.user.email ?? ''

  const [email, setEmail] = useState('')
  const [emailPassword, setEmailPassword] = useState('')
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [repeat, setRepeat] = useState('')
  const [emailNote, setEmailNote] = useState<Note>(null)
  const [passwordNote, setPasswordNote] = useState<Note>(null)
  const [busy, setBusy] = useState(false)

  const wantsEmail = email.trim() !== '' || emailPassword !== ''
  const wantsPassword = current !== '' || next !== '' || repeat !== ''

  async function changeEmail(): Promise<Note> {
    if (!email.trim()) return { ok: false, text: 'Wpisz nowy adres e-mail.' }
    if (email.trim().toLowerCase() === currentEmail.toLowerCase()) return { ok: false, text: 'To już jest Twój obecny adres.' }
    if (!emailPassword) return { ok: false, text: 'Wpisz obecne hasło — potwierdza, że to Ty.' }
    try {
      await api.studio.changeEmail(email.trim(), emailPassword)
      setEmail('')
      setEmailPassword('')
      refetch()
      return { ok: true, text: 'Adres zmieniony. Od teraz logujesz się nowym.' }
    } catch (err) {
      return { ok: false, text: emailError(err) }
    }
  }

  async function changePassword(): Promise<Note> {
    if (!current) return { ok: false, text: 'Wpisz obecne hasło.' }
    if (next.length < MIN_PASSWORD_LENGTH) return { ok: false, text: `Nowe hasło musi mieć co najmniej ${MIN_PASSWORD_LENGTH} znaków.` }
    if (next !== repeat) return { ok: false, text: 'Hasła nie są takie same.' }

    /**
     * better-auth handles this one end to end. `revokeOtherSessions` is the
     * reason to change a password at all: if someone else is signed in
     * somewhere, this is what throws them out. The current tab survives.
     */
    const { error } = await authClient.changePassword({ currentPassword: current, newPassword: next, revokeOtherSessions: true })
    // Both a wrong current password and a too-short new one come back as
    // 400, so the status alone would put the wrong message under the wrong
    // field. The code is what actually distinguishes them.
    if (error) return { ok: false, text: passwordError(error.code) }
    setCurrent('')
    setNext('')
    setRepeat('')
    return { ok: true, text: 'Hasło zmienione. Wszystkie inne zalogowane urządzenia zostały wylogowane.' }
  }

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (busy || (!wantsEmail && !wantsPassword)) return
    setBusy(true)
    // The e-mail first: its check uses the current password, which the
    // password change below may be about to replace.
    setEmailNote(wantsEmail ? await changeEmail() : null)
    setPasswordNote(wantsPassword ? await changePassword() : null)
    setBusy(false)
  }

  const short = next.length > 0 && next.length < MIN_PASSWORD_LENGTH ? MIN_PASSWORD_LENGTH - next.length : 0

  return (
    <Column
      density="studio"
      top={<TopTitle studio>Konto</TopTitle>}
      bottom={
        <>
          <span className="min-w-0 flex-1 text-class-gloss text-class-ink-soft">Po zmianie adresu logujesz się nowym.</span>
          <Button type="submit" form="account-form" disabled={busy || (!wantsEmail && !wantsPassword)} className="min-h-10 px-5 text-class-gloss">
            {busy ? 'Zapisuję…' : 'Zapisz zmiany'}
          </Button>
        </>
      }
    >
      {/* The mockup's 900px is the content box; Main counts its padding in. */}
      <Main density="studio" width={996}>
        <form id="account-form" onSubmit={save} className="flex flex-col">
          <h1 className="cl-title m-0 mb-8">Twoje konto</h1>

          <Section title="Adres e-mail" sub="Tym adresem logujesz się do Studia.">
            <Input id="account-current-email" label="Obecny adres" type="email" value={currentEmail} readOnly />
            <Input
              id="account-email"
              label="Nowy adres e-mail"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(v) => {
                setEmail(v)
                setEmailNote(null)
              }}
            />
            <Input
              id="account-email-password"
              label="Twoje obecne hasło"
              type="password"
              autoComplete="current-password"
              value={emailPassword}
              onChange={(v) => {
                setEmailPassword(v)
                setEmailNote(null)
              }}
              hint="Potrzebne, żeby potwierdzić zmianę."
            />
            <NoteLine note={emailNote} />
          </Section>

          <Section title="Hasło" sub={`Co najmniej ${MIN_PASSWORD_LENGTH} znaków.`}>
            <Input
              id="account-current-password"
              label="Obecne hasło"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(v) => {
                setCurrent(v)
                setPasswordNote(null)
              }}
            />
            <Input
              id="account-new-password"
              label="Nowe hasło"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(v) => {
                setNext(v)
                setPasswordNote(null)
              }}
              hint={short ? `Jeszcze ${short} ${plural(short, 'znak', 'znaki', 'znaków')}.` : undefined}
            />
            <Input
              id="account-repeat-password"
              label="Powtórz nowe hasło"
              type="password"
              autoComplete="new-password"
              value={repeat}
              onChange={(v) => {
                setRepeat(v)
                setPasswordNote(null)
              }}
            />
            <NoteLine note={passwordNote} />
          </Section>

          <Section title="Sesja" sub="Wyloguj się na tym urządzeniu." last>
            <button
              type="button"
              onClick={() => void signOutAndLeave()}
              className="cl-link self-start !text-class-wrong hover:!text-class-ink"
            >
              Wyloguj się ›
            </button>
          </Section>
        </form>
      </Main>
    </Column>
  )
}

type Note = { ok: boolean; text: string } | null

const plural = (n: number, one: string, few: string, many: string) =>
  n === 1 ? one : n % 10 >= 2 && n % 10 <= 4 && !(n % 100 >= 12 && n % 100 <= 14) ? few : many

function emailError(err: unknown): string {
  const code = err instanceof ApiError ? err.message : ''
  if (code === 'wrong_password') return 'Nieprawidłowe hasło.'
  if (code === 'email_taken') return 'Ten adres jest już zajęty.'
  if (code === 'same_email') return 'To już jest Twój obecny adres.'
  if (code === 'validation') return 'To nie wygląda na poprawny adres e-mail.'
  return 'Nie udało się zmienić adresu. Spróbuj jeszcze raz.'
}

function passwordError(code: string | undefined): string {
  if (code === 'INVALID_PASSWORD') return 'Nieprawidłowe obecne hasło.'
  if (code === 'PASSWORD_TOO_SHORT') return `Nowe hasło musi mieć co najmniej ${MIN_PASSWORD_LENGTH} znaków.`
  return 'Nie udało się zmienić hasła. Spróbuj jeszcze raz.'
}

/* ── shared bits ──────────────────────────────────────────────────────── */

/** A label column and a form column, a divider above. */
function Section({ title, sub, last = false, children }: { title: string; sub: string; last?: boolean; children: ReactNode }) {
  return (
    <section
      className={[
        'grid gap-x-12 gap-y-5 border-t border-class-line py-8 desk:grid-cols-[260px_minmax(0,1fr)] desk:py-9',
        last ? 'border-b' : '',
      ].join(' ')}
    >
      <div>
        <h2 className="cl-subheading m-0">{title}</h2>
        <p className="m-0 mt-1.5 text-class-gloss text-class-ink-faint">{sub}</p>
      </div>
      <div className="flex max-w-[420px] flex-col gap-[18px]">{children}</div>
    </section>
  )
}

function Input({
  id,
  label,
  type,
  value,
  onChange,
  autoComplete,
  readOnly = false,
  hint,
}: {
  id: string
  label: string
  type: 'email' | 'password'
  value: string
  onChange?: (value: string) => void
  autoComplete?: string
  readOnly?: boolean
  hint?: string
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-class-gloss font-semibold">
        {label}
      </label>
      <input
        id={id}
        type={type}
        autoComplete={autoComplete}
        value={value}
        readOnly={readOnly}
        onChange={(e) => onChange?.(e.target.value)}
        className={`cl-input ${readOnly ? 'cursor-default !bg-class-field-soft text-class-ink-soft focus:!border-class-sky-200 focus:!shadow-none' : ''}`}
      />
      {hint && <p className="m-0 mt-1.5 text-class-label text-class-ink-faint">{hint}</p>}
    </div>
  )
}

function NoteLine({ note }: { note: Note }) {
  if (!note) return null
  return (
    <p role={note.ok ? 'status' : 'alert'} className={`m-0 text-class-gloss font-semibold ${note.ok ? 'text-class-correct' : 'text-class-wrong'}`}>
      {note.ok ? '✓' : '✗'} {note.text}
    </p>
  )
}
